import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../../utils/enums';

const initialState = {
  // Lane data
  lanes: [],
  driveStartTime: null, // ISO string - needed to convert absolute line times back to video-relative seconds
  lanesStatus: RequestStatus.IDLE,
  lanesError: null,

  // Submit data
  submitStatus: RequestStatus.IDLE,
  submitError: null,
  submitResult: null,
};

const laneSlice = createSlice({
  name: 'lane',
  initialState,
  reducers: {
    // Lane data
    fetchLanesStart: (state) => {
      state.lanesStatus = RequestStatus.LOADING;
      state.lanesError = null;
    },
    // payload: { lanes: FormattedLane[], driveStartTime: string }
    fetchLanesSuccess: (state, action) => {
      state.lanesStatus = RequestStatus.SUCCEEDED;
      state.lanes = action.payload.lanes;
      state.driveStartTime = action.payload.driveStartTime;
      state.lanesError = null;
    },
    fetchLanesFailure: (state, action) => {
      state.lanesStatus = RequestStatus.FAILED;
      state.lanesError = action.payload;
    },
    submitLaneDataStart: (state) => {
      state.submitStatus = RequestStatus.LOADING;
      state.submitError = null;
      state.submitResult = null;
    },
    submitLaneDataSuccess: (state, action) => {
      state.submitStatus = RequestStatus.SUCCEEDED;
      state.submitResult = action.payload;
      state.submitError = null;
    },
    submitLaneDataFailure: (state, action) => {
      state.submitStatus = RequestStatus.FAILED;
      state.submitError = action.payload;
    },
    fetchLaneGpsDataStart: (state) => {
      state.lanesStatus = RequestStatus.LOADING;
      state.lanesError = null;
    },
    // payload: { lanes: FormattedLane[] (with gpsPoints attached), driveStartTime: string }
    fetchLaneGpsDataSuccess: (state, action) => {
      state.lanesStatus = RequestStatus.SUCCEEDED;
      const gpsLanes = Array.isArray(action.payload.lanes) ? action.payload.lanes : [];

      if (action.payload.driveStartTime) {
        state.driveStartTime = action.payload.driveStartTime;
      }

      // If we already have lanes loaded, merge the GPS-enriched `lines` in by lane ID;
      // otherwise take the GPS response as the base lane list.
      if (state.lanes && state.lanes.length > 0) {
        const gpsByLaneId = new Map(gpsLanes.map((gps) => [gps.laneid, gps]));

        state.lanes = state.lanes.map((lane) => {
          const gpsForLane = gpsByLaneId.get(lane.laneid);
          if (!gpsForLane) return lane;
          return {
            ...lane,
            lines: gpsForLane.lines || lane.lines,
          };
        });
      } else {
        state.lanes = gpsLanes;
      }
      state.lanesError = null;
    },
    fetchLaneGpsDataFailure: (state, action) => {
      state.lanesStatus = RequestStatus.FAILED;
      state.lanesError = action.payload;
    },

    // Reset state
    resetLaneState: () => initialState,

    // Reset submit status only
    resetSubmitStatus: (state) => {
      state.submitStatus = RequestStatus.IDLE;
      state.submitError = null;
      state.submitResult = null;
    },
  },
});

export const {
  fetchLanesStart,
  fetchLanesSuccess,
  fetchLanesFailure,
  submitLaneDataStart,
  submitLaneDataSuccess,
  submitLaneDataFailure,
  resetLaneState,
  resetSubmitStatus,
  fetchLaneGpsDataStart,
  fetchLaneGpsDataSuccess,
  fetchLaneGpsDataFailure,
} = laneSlice.actions;

export default laneSlice.reducer;
